import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function formatAge(seconds) {
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export default function Kitchen() {
  const { slug } = useParams();
  const { logout } = useAuth();
  const [orders, setOrders]         = useState([]);
  const [stations, setStations]     = useState([]);
  const [tick, setTick]             = useState(0);
  const [stationColor, setStationColor] = useState('#333');
  const [kds, setKds] = useState({
    kds_font_name: '24', kds_font_variant: '20',
    kds_color_ready: '#27AE60', kds_color_delivered: '#444444',
    kds_age_warn: '300', kds_age_danger: '600',
    kds_sound_enabled: '1', kds_grid_columns: 'auto', kds_show_category: '0',
  });
  const prevOrderIds                = useRef(new Set());
  const audioCtx                    = useRef(null);

  const currentStation = stations.find(s => s.slug === slug);
  const stationName    = currentStation?.name || slug;

  function ageColor(seconds) {
    if (seconds < parseInt(kds.kds_age_warn))   return '#3B6D11';
    if (seconds < parseInt(kds.kds_age_danger)) return '#854F0B';
    return '#A32D2D';
  }

  useEffect(() => {
    const token = localStorage.getItem('cafepos_token');
    fetch('/api/settings', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(s => setKds(prev => ({ ...prev, ...Object.fromEntries(
        Object.keys(prev).filter(k => s[k] !== undefined).map(k => [k, s[k]])
      ) })))
      .catch(() => {});
  }, []);

  function playBeep() {
    // Globalne ustawienie (Ustawienia -> KDS) to "master switch" - jeśli wyłączone,
    // dźwięk nigdzie się nie odezwie. Jeśli włączone, decyduje jeszcze ustawienie
    // konkretnego stanowiska (Stanowiska kuchenne -> Edytuj -> Dźwięk). Widok
    // "zbiorczy" nie ma dopasowanego stanowiska - domyślnie dźwięk włączony.
    if (kds.kds_sound_enabled !== '1') return;
    const stationSoundEnabled = currentStation ? !!currentStation.sound_enabled : true;
    if (!stationSoundEnabled) return;
    try {
      if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtx.current;
      [0, 150, 300].forEach(delay => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.3, ctx.currentTime + delay / 1000);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay / 1000 + 0.2);
        osc.start(ctx.currentTime + delay / 1000);
        osc.stop(ctx.currentTime + delay / 1000 + 0.25);
      });
    } catch {}
  }

  useEffect(() => {
    fetch('/api/kitchen/stations').then(r => r.json()).then(data => {
      setStations(data);
      const station = data.find(s => s.slug === slug);
      if (station?.color) setStationColor(station.color);
    }).catch(() => {});
  }, [slug]);

  const fetchOrders = useCallback(async () => {
    try {
      const res  = await fetch(`/api/kitchen/orders/${slug}`);
      const data = await res.json();
      const newIds = new Set(data.map(o => o.id));
      const hasNew = data.some(o => !prevOrderIds.current.has(o.id));
      if (hasNew && prevOrderIds.current.size > 0) playBeep();
      prevOrderIds.current = newIds;
      setOrders(data);
    } catch {}
  }, [slug]);

  useEffect(() => {
    fetchOrders();
    const poll = setInterval(fetchOrders, 5000);
    return () => clearInterval(poll);
  }, [fetchOrders]);

  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  async function markItemReady(itemId, orderId) {
    const token = localStorage.getItem('cafepos_token');
    await fetch(`/api/kitchen/items/${itemId}/ready`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    setOrders(prev => prev.map(o => o.id === orderId ? {
      ...o,
      items: o.items.map(i => i.order_item_id === itemId ? { ...i, item_kitchen_status: 'ready' } : i),
    } : o));
    fetchOrders();
  }

  async function markAllReady(orderId) {
    const token = localStorage.getItem('cafepos_token');
    await fetch(`/api/kitchen/orders/${orderId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ kitchen_status: 'ready', slug }),
    });
    fetchOrders();
  }

  const [confirmDeliver, setConfirmDeliver] = useState(null);
  const [confirmDeliverAll, setConfirmDeliverAll] = useState(null); // orderId // { itemId, itemName, orderId }

  async function deliverItem(itemId, itemName, orderId) {
    setConfirmDeliver({ itemId, itemName, orderId });
  }

  async function confirmDeliverAllItems() {
    const orderId = confirmDeliverAll;
    setConfirmDeliverAll(null);
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    const token = localStorage.getItem('cafepos_token');
    // Wydaj wszystkie gotowe pozycje naraz
    await Promise.all(
      order.items
        .filter(i => i.item_kitchen_status === 'ready' && i.item_delivery_status !== 'delivered')
        .map(i => fetch(`/api/kitchen/items/${i.order_item_id}/deliver`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        }))
    );
    // Odświeżenie
    setOrders(prev => prev.map(o => o.id !== orderId ? o : {
      ...o,
      items: o.items.map(i => i.item_kitchen_status === 'ready' ? { ...i, item_delivery_status: 'delivered' } : i),
    }));
  }

  async function confirmDeliverItem() {
    const { itemId, orderId } = confirmDeliver;
    setConfirmDeliver(null);
    await fetch(`/api/kitchen/items/${itemId}/deliver`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('cafepos_token')}` },
    });
    setOrders(prev => prev.map(o => o.id !== orderId ? o : {
      ...o,
      items: o.items.map(i => i.order_item_id === itemId ? { ...i, item_delivery_status: 'delivered' } : i),
    }));
  }

  async function deliverReady(orderId) {
    const token = localStorage.getItem('cafepos_token');
    await fetch(`/api/kitchen/orders/${orderId}/deliver-ready`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ slug }),
    });
    fetchOrders();
  }

  async function markInProgress(orderId) {
    const token = localStorage.getItem('cafepos_token');
    await fetch(`/api/kitchen/orders/${orderId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ kitchen_status: 'preparing' }),
    });
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, kitchen_status: 'preparing' } : o));
  }

  return (
    <div style={{ minHeight: '100vh', background: '#1a1a1a', color: '#fff', fontFamily: 'sans-serif' }}>
      <div style={{ background: '#111', borderBottom: '3px solid ' + stationColor, padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: stationColor }} />
          <span style={{ fontSize: 20, fontWeight: 700 }}>{stationName}</span>
          <span style={{ fontSize: 13, color: '#888', marginLeft: 8 }}>
            {orders.length} {orders.length === 1 ? 'zamówienie' : 'zamówień'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {stations.map(s => (
            <Link key={s.slug} to={`/kitchen/${s.slug}`} style={{
              padding: '5px 12px', borderRadius: 6, fontSize: 12, textDecoration: 'none',
              background: s.slug === slug ? stationColor : '#333',
              color: s.slug === slug ? '#fff' : '#aaa',
            }}>{s.name}</Link>
          ))}
          <Link to="/" style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12, textDecoration: 'none', background: '#333', color: '#aaa', marginLeft: 8 }}>
            ← POS
          </Link>
          <button onClick={logout} style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12, background: '#333', color: '#aaa', border: 'none', cursor: 'pointer', marginLeft: 4 }}>
            Wyloguj
          </button>
        </div>
      </div>

      {orders.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 56px)', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 48 }}>✓</div>
          <div style={{ fontSize: 18, color: '#555' }}>Brak oczekujących zamówień</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: kds.kds_grid_columns === 'auto' ? 'repeat(auto-fill, minmax(280px, 1fr))' : `repeat(${kds.kds_grid_columns}, 1fr)`, gap: 16, padding: 20 }}>
          {orders.map(order => {
            const realAge   = Math.floor((Date.now() - new Date(order.created_at)) / 1000);
            const isNew     = order.kitchen_status === 'waiting';
            const isReady   = order.kitchen_status === 'preparing';
            const isDone    = order.kitchen_status === 'ready';
            const isPaid    = order.payment_status === 'paid';
            const allItemsReady = order.items.every(i => i.item_kitchen_status === 'ready');
            const someItemsReady = order.items.some(i => i.item_kitchen_status === 'ready');

            return (
              <div key={order.id} style={{
                background: '#242424',
                border: `1px solid #444`,
                borderRadius: 12,
                overflow: 'hidden',
              }}>
                <div style={{ background: '#1a1a1a', padding: '10px 14px', borderBottom: '1px solid #333' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isPaid && <span style={{ background: '#27AE60', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>OPŁACONE</span>}
                      <span style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>Stolik {order.table_number}</span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: ageColor(realAge) }}>
                      {formatAge(realAge + tick * 0)}
                    </span>
                  </div>
                  {order.notes && (
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#ffcd42', marginTop: 6, background: '#332b0a', padding: '5px 9px', borderRadius: 6 }}>
                      ⚠ {order.notes}
                    </div>
                  )}
                </div>

                <div style={{ padding: '10px 14px' }}>
                  {order.items.map((item, i) => {
                    const itemReady     = item.item_kitchen_status === 'ready';
                    const itemDelivered = item.item_delivery_status === 'delivered';
                    return (
                      <div key={i}
                        onClick={() => {
                          if (itemDelivered) return;
                          if (itemReady) deliverItem(item.order_item_id, item.name, order.id);
                          else markItemReady(item.order_item_id, order.id);
                        }}
                        style={{
                          display: 'flex', alignItems: 'baseline', gap: 8,
                          padding: '8px 6px', borderBottom: i < order.items.length - 1 ? '1px solid #333' : 'none',
                          cursor: itemDelivered ? 'default' : 'pointer',
                          opacity: itemDelivered ? 0.3 : 1,
                          borderRadius: 6,
                          background: itemDelivered ? '#0a1a0a' : itemReady ? '#1a2a1a' : 'transparent',
                          transition: 'background .15s',
                        }}
                      >
                        <span style={{ fontSize: parseInt(kds.kds_font_name), fontWeight: 700, minWidth: 36, color: itemDelivered ? kds.kds_color_delivered : itemReady ? kds.kds_color_ready : '#fff' }}>
                          {item.qty}×
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: parseInt(kds.kds_font_name), fontWeight: 500, color: itemDelivered ? kds.kds_color_delivered : itemReady ? '#888' : '#fff', textDecoration: itemDelivered ? 'line-through' : 'none' }}>
                            {item.name}
                          </div>
                          {item.variant_label && (
                            <div style={{ fontSize: parseInt(kds.kds_font_variant), color: itemDelivered ? '#333' : itemReady ? '#444' : '#fff', marginTop: 2 }}>{item.variant_label}</div>
                          )}
                          {(item.modifier_names || []).map((mod, mi) => (
                            <div key={mi} style={{ fontSize: parseInt(kds.kds_font_variant), color: itemDelivered ? '#333' : '#fff', marginTop: 2 }}>+ {mod}</div>
                          ))}
                          {item.notes && (
                            <div style={{ fontSize: parseInt(kds.kds_font_variant), fontWeight: 600, color: itemDelivered ? '#333' : '#ffcd42', marginTop: 2 }}>⚠ {item.notes}</div>
                          )}
                          {kds.kds_show_category === '1' && item.category && (
                            <div style={{ fontSize: parseInt(kds.kds_font_variant) - 6, color: itemDelivered ? '#333' : '#888', marginTop: 2 }}>{item.category}</div>
                          )}
                        </div>
                        {itemDelivered && <span style={{ fontSize: 18, color: kds.kds_color_delivered }}>✓✓</span>}
                        {!itemDelivered && itemReady && <span style={{ fontSize: 11, color: kds.kds_color_ready }}>wydaj</span>}
                        {!itemDelivered && !itemReady && <span style={{ fontSize: 11, color: '#555' }}>kliknij</span>}
                      </div>
                    );
                  })}
                </div>

                {!allItemsReady && (
                  <div style={{ display: 'flex', gap: 8, padding: '10px 14px', borderTop: '1px solid #333', flexWrap: 'wrap' }}>


                    <button onClick={() => markAllReady(order.id)} style={{
                      flex: 2, padding: '8px', background: stationColor, border: 'none', borderRadius: 7,
                      color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', minWidth: 100,
                    }}>
                      ✓ Gotowe wszystko
                    </button>
                  </div>
                )}
                {allItemsReady && (
                  <div style={{ padding: '10px 14px', borderTop: '1px solid #333' }}>
                    <button onClick={() => setConfirmDeliverAll(order.id)}
                      style={{ width: '100%', padding: '12px', border: 'none', borderRadius: 8, background: '#27AE60', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                      🍽️ Wydaj wszystko
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {confirmDeliverAll && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 14, padding: 28, width: 320, maxWidth: '90vw', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🍽️</div>
            <div style={{ fontSize: 17, fontWeight: 600, color: '#fff', marginBottom: 8 }}>Wydać wszystkie gotowe pozycje?</div>
            <div style={{ fontSize: 14, color: '#aaa', marginBottom: 24 }}>Stolik {orders.find(o => o.id === confirmDeliverAll)?.table_number}</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setConfirmDeliverAll(null)}
                style={{ flex: 1, padding: '12px', border: '1px solid #444', borderRadius: 8, background: 'transparent', color: '#aaa', fontSize: 14, cursor: 'pointer' }}>
                Anuluj
              </button>
              <button onClick={confirmDeliverAllItems}
                style={{ flex: 2, padding: '12px', border: 'none', borderRadius: 8, background: '#27AE60', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                ✓ Wydaj wszystko
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDeliver && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 14, padding: 28, width: 320, maxWidth: '90vw', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🍽️</div>
            <div style={{ fontSize: 17, fontWeight: 600, color: '#fff', marginBottom: 8 }}>Wydać pozycję?</div>
            <div style={{ fontSize: 14, color: '#aaa', marginBottom: 24 }}>{confirmDeliver.itemName}</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setConfirmDeliver(null)}
                style={{ flex: 1, padding: '12px', border: '1px solid #444', borderRadius: 8, background: 'transparent', color: '#aaa', fontSize: 14, cursor: 'pointer' }}>
                Anuluj
              </button>
              <button onClick={confirmDeliverItem}
                style={{ flex: 2, padding: '12px', border: 'none', borderRadius: 8, background: '#27AE60', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                ✓ Wydano
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
